// An invitation draft (ruling C): ONE flat fee and its currency, the paid
// interval, the required boarding and return times, the adjustment rules, the
// payment timing, the terms version, when the invitation expires and how long
// the ambassador is held after she accepts. Every field is visible and
// required; once sent, they are frozen (terms_hash) and a change means withdraw
// and invite again, which she must accept again.
//
// Saving a draft is a draft write (an assistant may prepare one, H). Sending is
// the coordinator's own action (InvitationRow).
//
// Choosing the BA (owner, 4 Nov 2026): a picker over the slot's bookable list
// (GET /booking-slots/{id}/candidates, services:booking:manage) — no typed ids
// and no portfolio-reviewer permission. Each BA shows her approved name, skills,
// service areas, "training not checked" and availability for the booking's
// window: Free, Busy (with the conflicting window) or Unknown, each looking
// different. Busy and unknown can still be chosen — commerce checks again at
// send and confirm — but are clearly marked. The draft names her by profileId
// only; commerce resolves the employee.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { DataState } from '@/features/shared/components/ui/DataState';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  type Booking, type BookingCandidate, type Invitation, type InvitationDraft, type Slot, type Terms, createInvitation,
  errorCode, listSlotCandidates, updateInvitation,
} from '@/services/baBookingService';
import { isoToZonedLocal, minorToMajorText, parseMajorToMinor, zonedLocalToIso } from './bookingFormat';
import { AvailabilityBadge } from './ConfirmPanel';
import { Field, type K, NativeSelect, useRunAction } from './shared';

export interface InvitationFormValues {
  profileId: string; fee: string; currency: string;
  paidStart: string; paidEnd: string; boarding: string; ret: string;
  adjustmentRules: string; paymentTiming: string; termsVersionId: string; expires: string; holdUntil: string;
}

/** Which required fields are still empty or invalid (keys of the form). */
export function missingInvitationFields(v: InvitationFormValues): (keyof InvitationFormValues)[] {
  const m: (keyof InvitationFormValues)[] = [];
  (['profileId', 'currency', 'paidStart', 'paidEnd', 'boarding', 'ret', 'expires', 'holdUntil'] as const)
    .forEach((f) => { if (!v[f].trim()) m.push(f); });
  if (!v.adjustmentRules.trim()) m.push('adjustmentRules');
  if (!v.paymentTiming.trim()) m.push('paymentTiming');
  if (!v.termsVersionId) m.push('termsVersionId');
  const fee = parseMajorToMinor(v.fee, v.currency || 'TZS');
  if (fee == null || fee <= 0) m.push('fee');
  return m;
}

export const InvitationForm: React.FC<{
  booking: Booking; slot: Slot; k: K; activeTerms: Terms[];
  existing?: Invitation; defaultCurrency?: string; onSaved: (i: Invitation) => void; onCancel: () => void;
  /** Called with the bookable list once loaded, so rows can show names. */
  onCandidates?: (c: BookingCandidate[]) => void;
}> = ({ booking, slot, k, activeTerms, existing, defaultCurrency, onSaved, onCancel, onCandidates }) => {
  const tz = booking.timezone;
  const { busy, run } = useRunAction(k);
  const [v, setV] = useState<InvitationFormValues>(() => {
    const cur = existing?.currency || defaultCurrency || 'TZS';
    return {
      profileId: existing?.profileId ?? '',
      fee: existing ? minorToMajorText(existing.feeAmountMinor, cur) : '', currency: cur,
      paidStart: isoToZonedLocal(existing?.paidStartAt ?? slot.paidStartAt, tz),
      paidEnd: isoToZonedLocal(existing?.paidEndAt ?? slot.paidEndAt, tz),
      boarding: isoToZonedLocal(existing?.boardingAt ?? booking.boardingAt, tz),
      ret: isoToZonedLocal(existing?.returnAt ?? booking.returnAt, tz),
      adjustmentRules: existing?.adjustmentRules ?? '', paymentTiming: existing?.paymentTiming ?? '',
      termsVersionId: existing?.termsVersionId ?? (activeTerms.length === 1 ? activeTerms[0].id : ''),
      expires: isoToZonedLocal(existing?.expiresAt, tz), holdUntil: isoToZonedLocal(existing?.holdUntil, tz),
    };
  });
  const set = (f: keyof InvitationFormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setV((s) => ({ ...s, [f]: e.target.value }));
  const missing = useMemo(() => missingInvitationFields(v), [v]);
  // A draft may wait for terms; everything else is needed to save.
  const blocking = missing.filter((f) => f !== 'termsVersionId');
  const id = (f: string) => `inv-${slot.id}-${f}`;
  const [cands, setCands] = useState<{ s: 'loading' } | { s: 'ok'; list: BookingCandidate[] } | { s: 'error'; msg: string }>({ s: 'loading' });
  const report = useRef(onCandidates);
  report.current = onCandidates;
  const loadCandidates = useCallback(() => {
    setCands({ s: 'loading' });
    listSlotCandidates(slot.id)
      .then((d) => { setCands({ s: 'ok', list: d.candidates }); report.current?.(d.candidates); })
      // 503 = the catalogue can't be read: say so as the backend does — never an
      // empty list, as if there were no ambassadors.
      .catch((e) => setCands({ s: 'error', msg: getApiErrorMessage(e, k('candidates.error', 'Could not load the Brand Ambassadors you can invite.')) }));
  }, [slot.id, k]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);
  const list = cands.s === 'ok' ? cands.list : [];
  const chosenGone = cands.s === 'ok' && !!v.profileId && !list.some((c) => c.profileId === v.profileId);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (blocking.length) return;
    const body: InvitationDraft = {
      profileId: v.profileId.trim(),
      feeAmountMinor: parseMajorToMinor(v.fee, v.currency) as number, currency: v.currency.trim().toUpperCase(),
      paidStartAt: zonedLocalToIso(v.paidStart, tz), paidEndAt: zonedLocalToIso(v.paidEnd, tz),
      boardingAt: zonedLocalToIso(v.boarding, tz), returnAt: zonedLocalToIso(v.ret, tz),
      adjustmentRules: v.adjustmentRules.trim(), paymentTiming: v.paymentTiming.trim(),
      expiresAt: zonedLocalToIso(v.expires, tz), holdUntil: zonedLocalToIso(v.holdUntil, tz),
      ...(v.termsVersionId ? { termsVersionId: v.termsVersionId } : {}),
    };
    const out = await run(() => (existing
      ? updateInvitation(existing.id, existing.version, body)
      : createInvitation(slot.id, body)), k('invitation.saved', 'Invitation draft saved.'),
    // 409 ba_unavailable / 422 ba_mismatch / 503 are toasted as the backend
    // words them; she may have left the catalogue, so read the list again.
    (err) => { if (errorCode(err) === 'ba_unavailable') loadCandidates(); return false; });
    if (out) onSaved(out);
  };

  const req = (label: string) => `${label} *`;
  return (
    <form onSubmit={save} className="space-y-3 rounded-md border border-border p-3" data-testid="invitation-form">
      <p className="text-xs text-muted-foreground">
        {k('invitation.help', 'Every field is required. When you send the invitation these terms are frozen; to change any of them, withdraw it and send a new one, which the ambassador must accept again. Times are in {{tz}}.', { tz })}
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <fieldset className="md:col-span-2 space-y-2" data-testid="ba-picker">
          <legend className="text-sm font-medium text-foreground">{req(k('invitation.ba', 'Brand Ambassador'))}</legend>
          <p className="text-xs text-muted-foreground">
            {k('candidates.help', 'Published Brand Ambassadors, with their availability for this booking. Busy or unknown ambassadors can still be chosen; it is checked again when you send and when you confirm.')}
          </p>
          {cands.s === 'loading' && <DataState variant="loading" compact title={k('candidates.loading', 'Checking who is available…')} />}
          {cands.s === 'error' && <DataState variant="error" compact message={cands.msg} onRetry={loadCandidates} />}
          {cands.s === 'ok' && list.length === 0 && (
            <DataState variant="empty" compact title={k('candidates.empty', 'No published Brand Ambassadors to invite yet.')} />
          )}
          {chosenGone && (
            <p className="text-xs text-warning-strong" data-testid="ba-gone">
              {k('candidates.chosen_gone', 'The ambassador on this draft is no longer in the bookable list. Choose another.')}
            </p>
          )}
          {list.length > 0 && (
            <ul className="space-y-2" role="radiogroup" aria-label={k('invitation.ba', 'Brand Ambassador')}>
              {list.map((c) => {
                const checked = v.profileId === c.profileId;
                return (
                  <li key={c.profileId}>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${checked ? 'border-primary-500 bg-muted' : 'border-border'}`}
                      data-testid={`candidate-${c.profileId}`} data-availability={c.availability}>
                      <input type="radio" name={id('ba')} value={c.profileId} checked={checked} className="mt-1"
                        onChange={() => setV((s) => ({ ...s, profileId: c.profileId }))} />
                      <span className="flex-1 space-y-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-foreground">{c.displayName || k('candidates.unnamed', 'Brand Ambassador')}</span>
                          <AvailabilityBadge slot={c} tz={tz} k={k} />
                        </span>
                        {c.skills.length > 0 && (
                          <span className="block text-xs text-muted-foreground">{k('slots.skills_list', 'Skills: {{skills}}', { skills: c.skills.join(', ') })}</span>
                        )}
                        {c.serviceAreas.length > 0 && (
                          <span className="block text-xs text-muted-foreground">{k('candidates.areas', 'Service areas: {{areas}}', { areas: c.serviceAreas.join(', ') })}</span>
                        )}
                        <span className="block text-xs text-muted-foreground">{k('slots.training', 'Training: not checked in the pilot')}</span>
                        {c.availability !== 'free' && (
                          <span className="block text-xs text-warning-strong" data-testid="candidate-caution">
                            {c.availability === 'busy'
                              ? k('candidates.busy_note', 'Busy in this booking’s window. Sending will be refused unless she is free by then.')
                              : k('candidates.unknown_note', 'We couldn’t read her leave or assignments, so we can’t say she is free. It is checked again when you send.')}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </fieldset>
        <Field label={req(k('invitation.fee', 'Flat fee for this shift'))} htmlFor={id('fee')}>
          <Input id={id('fee')} inputMode="decimal" value={v.fee} onChange={set('fee')} required />
        </Field>
        <Field label={req(k('invitation.currency', 'Currency'))} htmlFor={id('cur')}>
          <Input id={id('cur')} value={v.currency} maxLength={3} onChange={set('currency')} required />
        </Field>
        <Field label={req(k('invitation.paid_start', 'Paid interval starts'))} htmlFor={id('ps')}>
          <Input id={id('ps')} type="datetime-local" value={v.paidStart} onChange={set('paidStart')} required />
        </Field>
        <Field label={req(k('invitation.paid_end', 'Paid interval ends'))} htmlFor={id('pe')}>
          <Input id={id('pe')} type="datetime-local" value={v.paidEnd} onChange={set('paidEnd')} required />
        </Field>
        <Field label={req(k('invitation.boarding', 'Required boarding time'))} htmlFor={id('bo')}>
          <Input id={id('bo')} type="datetime-local" value={v.boarding} onChange={set('boarding')} required />
        </Field>
        <Field label={req(k('invitation.return', 'Required return time'))} htmlFor={id('re')}>
          <Input id={id('re')} type="datetime-local" value={v.ret} onChange={set('ret')} required />
        </Field>
        <Field label={req(k('invitation.adjustment_rules', 'Adjustment rules'))} htmlFor={id('adj')} className="md:col-span-2"
          hint={k('invitation.adjustment_hint', 'How extra time or changes are approved and paid. Adjustments are approved separately.')}>
          <Textarea id={id('adj')} value={v.adjustmentRules} maxLength={2000} onChange={set('adjustmentRules')} required />
        </Field>
        <Field label={req(k('invitation.payment_timing', 'Payment timing'))} htmlFor={id('pt')} className="md:col-span-2"
          hint={k('invitation.payment_timing_hint', 'When the ambassador is paid for this shift.')}>
          <Textarea id={id('pt')} value={v.paymentTiming} maxLength={2000} onChange={set('paymentTiming')} required />
        </Field>
        <Field label={req(k('invitation.terms', 'Cancellation and no-show terms version'))} htmlFor={id('tv')} className="md:col-span-2"
          hint={activeTerms.length === 0 ? k('confirm.no_active_terms', 'Waiting for Fern’s cancellation and no-show terms') : undefined}>
          <NativeSelect id={id('tv')} value={v.termsVersionId} onChange={set('termsVersionId')} disabled={activeTerms.length === 0}>
            <option value="">{k('common.choose', 'Choose…')}</option>
            {activeTerms.map((t) => <option key={t.id} value={t.id}>{`${t.title} (v${t.versionNo})`}</option>)}
          </NativeSelect>
        </Field>
        <Field label={req(k('invitation.expires', 'Invitation expires'))} htmlFor={id('ex')}>
          <Input id={id('ex')} type="datetime-local" value={v.expires} onChange={set('expires')} required />
        </Field>
        <Field label={req(k('invitation.hold_until', 'Hold the ambassador until'))} htmlFor={id('hu')}
          hint={k('invitation.hold_hint', 'After she accepts, her time is held until then. No later than boarding.')}>
          <Input id={id('hu')} type="datetime-local" value={v.holdUntil} onChange={set('holdUntil')} required />
        </Field>
      </div>
      {blocking.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="invitation-missing">
          {k('invitation.missing', 'Fill in every field to save.')}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || blocking.length > 0}>{k('invitation.save', 'Save invitation draft')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{k('common.cancel', 'Cancel')}</Button>
      </div>
    </form>
  );
};

export default InvitationForm;
